<template>
  <modal :open="internalOpen" :width="1400">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ entityChange.type }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <div class="flex w-full">
        <field
          :label="$t('entityChange.className')"
          class="text-left w-1/3 no-border"
        >
          {{ entityChange.className }}
        </field>
        <field
          :label="$t('entityChange.entityId')"
          class="text-left w-1/3 no-border"
        >
          {{ entityChange.entityId }}
        </field>
        <field
          :label="$t('entityChange.date')"
          class="text-left w-1/3 no-border"
        >
          {{
            format(new Date(entityChange.date), 'dd.MM.yyyy - HH:mm:ss')
          }}
        </field>
      </div>
      <div class="flex w-full mt-5 mb-10">
        <field
          :label="$t('user.firstName')"
          class="text-left w-1/3 no-border"
        >
          {{ entityChange.user.firstName }}
        </field>
        <field
          :label="$t('user.lastName')"
          class="text-left w-1/3 no-border"
        >
          {{ entityChange.user.lastName }}
        </field>
        <field
          :label="$t('user.role')"
          class="text-left w-1/3 no-border"
        >
          {{ entityChange.user.roles
          .filter((el) => el !== 'ROLE_USER')
          .map((item) => t(`user.roles.${item}`))
          .join(', ') }}
        </field>
      </div>
      <data-table
        class="mb-auto"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
      />
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import {format} from "date-fns";
import FormInput from '@utils/inputs/Input.vue';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import type { EntityChange } from '@/entities/Entities';
import DataTable, { type Column } from '@components/utils/table/Table.vue';
import type { Filter } from '@/components/utils/filter/Filter.vue';
import { computed, ref } from 'vue';
import Field from "@utils/inputs/Field.vue";
import {useQueryRef} from '@hooks/url';
import {useTableFields} from '@hooks/table-configuration';

interface Props {
  open: boolean;
  entityChange: EntityChange;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();

const notify = useNotify();

function closeModal() {
  internalOpen.value = false;
}

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'propertyName',
    title: t('entityChange.propertyName'),
    sortable: false,
    visible: true,
  },
  {
    key: 'beforeChange',
    title: t('entityChange.beforeChange'),
    sortable: false,
    visible: true,
  },
  {
    key: 'afterChange',
    title: t('entityChange.afterChange'),
    sortable: false,
    visible: true,
  },
];

const visibleColumns = useTableFields(
    columns,
);

const records = computed(() =>
  Object.keys(props.entityChange.changes).map((key) =>
    props.entityChange.type === 'update'
      ? {
          propertyName: key,
          beforeChange: props.entityChange?.changes?.[key]?.[0],
          afterChange: props.entityChange?.changes?.[key]?.[1],
        }
      : props.entityChange.type === 'create'
      ? {
          propertyName: key,
          beforeChange: '-',
          afterChange: props.entityChange?.changes[key],
        }
      : {
          propertyName: key,
          beforeChange: props.entityChange?.changes[key],
          afterChange: '-',
        }
  )
);
// const records = computed(() => Object.entries(props.entityChange.changes).map((val) => ));
</script>

<style lang="scss" scoped>
.no-border{
  :deep(.field){
    border: none;
  }
}
</style>
